import * as THREE from 'three';
import { animateTransform } from '../scene-transforms.js';
import type { MapDefinition } from '../../../shared/game/world-types.js';
import { SceneryBatches, seededRandom } from './scenery-batches.js';
import type { AnimatedProp } from './coast-props.js';
import { createFishingBoat } from './dressing-specimens.js';
import type { WaterContact } from './water-surface.js';

/**
 * Everything beyond the playable rectangle: open sea past the south-west edge, dusky headlands on the
 * land sides, sea stacks and distant fishing lights. It is presentation only — the camera is clamped to
 * the map, so none of it can be reached or collided with.
 */
export function createOuterWorld(scene: THREE.Scene, map: MapDefinition, water: THREE.Material, rock: THREE.Material, batches: SceneryBatches, contacts: WaterContact[]): AnimatedProp {
  const random = seededRandom(map.scenery!.seed ^ 0x5a17), half = { x: map.width / 2, z: map.depth / 2 };
  const southShore = map.scenery!.shoreline.at(-1)!, southShoreX = southShore.x + southShore.width / 2;

  let skirtGeometry: THREE.BufferGeometry;
  if (map.terrain) {
    // Keep the backdrop outside the playable mesh: a lowered hollow must not reveal a flat sheet above it.
    const outerX = half.x + 1300, outerZ = half.z + 1200;
    const shape = new THREE.Shape([new THREE.Vector2(-outerX, -outerZ), new THREE.Vector2(outerX, -outerZ), new THREE.Vector2(outerX, outerZ), new THREE.Vector2(-outerX, outerZ)]);
    shape.holes.push(new THREE.Path([new THREE.Vector2(-half.x, -half.z), new THREE.Vector2(-half.x, half.z), new THREE.Vector2(half.x, half.z), new THREE.Vector2(half.x, -half.z)]));
    skirtGeometry = new THREE.ShapeGeometry(shape).rotateX(-Math.PI / 2);
  } else skirtGeometry = new THREE.PlaneGeometry(map.width + 2600, map.depth + 2400).rotateX(-Math.PI / 2);
  const skirt = new THREE.Mesh(skirtGeometry,
    new THREE.MeshStandardMaterial({ color: 0x6a5f55, roughness: 1 }));
  skirt.position.y = -.08; skirt.receiveShadow = true; scene.add(skirt);

  const sea = (x0: number, x1: number, z0: number, z1: number) => {
    // The sea shader reads local coordinates as world coordinates, so the position is baked into the geometry.
    scene.add(new THREE.Mesh(new THREE.PlaneGeometry(x1 - x0, z1 - z0).rotateX(-Math.PI / 2).translate((x0 + x1) / 2, .085, (z0 + z1) / 2), water));
  };
  sea(-half.x - 1500, -half.x + 2, -half.z - 1200, half.z + 1200);
  sea(-half.x, southShoreX, half.z - 2, half.z + 1200);

  // Headlands: huge low-poly mounds that the fog melts into violet silhouettes.
  const hill = new THREE.DodecahedronGeometry(1, 0);
  const tones = [0x6d5a73, 0x7a6173, 0x5f5870, 0x84687a];
  for (let i = 0; i < 90; i++) {
    const side = i % 3, radius = 55 + random() * 120, height = 25 + random() * 85;
    // The whole mound starts beyond the playable edge (centre = edge + radius + gap), so no slope ever covers the map.
    const depth = radius * 1.4 + 30 + random() * 260;
    let x: number, z: number;
    if (side === 0) { x = -half.x + random() * map.width * 1.1; z = -half.z - depth; }
    else if (side === 1) { x = half.x + depth; z = -half.z - 200 + random() * (map.depth + 400); }
    else { x = southShoreX + radius + random() * (half.x - southShoreX + 300); z = half.z + depth; }
    batches.add('headland', hill, rock, x, height * .25, z, radius, height * (1 - depth / 900), radius * (.7 + random() * .6), random() * 6, tones[i % tones.length], false);
  }

  // Sea stacks and skerries stand in the surf off the south-west shore.
  const stacks: THREE.Vector3[] = [];
  for (let i = 0; i < 26; i++) {
    const x = -half.x - 25 - random() * 420, z = -half.z + random() * (map.depth + 600), s = 3 + random() * 7;
    stacks.push(new THREE.Vector3(x, 0, z));
    contacts.push({ x, z, angle: 0, halfX: s * .95, halfZ: s * .9, rock: true });
    batches.add('sea-stack', hill, rock, x, s * .9, z, s, s * (1.5 + random() * 2.2), s * .9, random() * 6, 0x5d5964, true);
    batches.add('sea-stack-cap', hill, rock, x, s * 2.6, z, s * .55, s * .5, s * .55, random() * 6, 0x8a8470, false);
  }
  // Skerries just offshore of the southern beaches.
  for (let i = 0; i < 18; i++) {
    const x = southShoreX - 20 - random() * 280, z = half.z + 8 + random() * 120, s = 1.5 + random() * 3.2;
    contacts.push({ x, z, angle: 0, halfX: s * 1.3, halfZ: s * .95, rock: true });
    batches.add('skerry', hill, rock, x, s * .25, z, s * 1.4, s * .6, s, random() * 6, 0x7d756b, false);
  }

  // A distant fishing fleet rides at anchor; their lanterns bob and twinkle on the water.
  const fleet: { group: THREE.Group; base: THREE.Vector3; light: THREE.Mesh; phase: number }[] = [];
  for (const [x, z] of [[-690, -250], [-760, 40], [-640, 215], [-540, 560], [-330, 640], [-820, -120]]) {
    const { group, light } = createFishingBoat(); group.position.set(x, .1, z); group.rotation.y = random() * 6;
    scene.add(group); fleet.push({ group, base: group.position.clone(), light, phase: random() * 6 });
  }
  return { update: time => {
    for (const boat of fleet) {
      boat.group.position.y = boat.base.y + Math.sin(time * .9 + boat.phase) * .08;
      boat.group.rotation.z = Math.sin(time * .7 + boat.phase) * .025; boat.group.rotation.x = Math.sin(time * .55 + boat.phase * 2) * .02;
      boat.light.scale.setScalar(.85 + Math.sin(time * 5 + boat.phase) * .15);
    }
  } };
}
